import { Col, Row } from 'react-bootstrap';
import { FormCheckbox, FormInput, FormNumberInput, FormSelect } from '@components/Common/AwpFormInputs';
import React from 'react';
import * as Yup from 'yup';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { isUnique } from '@awp/helpers/Utils';
import { CrudService, EntityProps } from '@awp/shared/CrudService';
import { ENTITY_TITLES } from '@awp/constants';

export class StandardGraphAxisService extends CrudService {
  constructor(mode: string) {
    const entityName = ENTITY_TITLES.STANDARD_GRAPH_AXIS;
    const entityPluralName = ENTITY_TITLES.STANDARD_GRAPH_AXIS;

    super({ entityName, entityPluralName, mode });
  }

  static getValidations(data: any, allStandardGraphAxises: any) {
    return Yup.object({
      Name: Yup.string()
        .max(250, 'Maximum 250 characters are allowed')
        .required('Name is a required field')
        .test('Unique Name', 'Name must be unique', function (value) {
          return isUnique(data, allStandardGraphAxises, 'Name', value);
        }),
      unit: Yup.object().required('Unit is a required field').nullable(),
    });
  }

  static getFields({
    props,
    readOnly,
    unitsOfMeasureDropdown = [],
    setEnabledIsMinimumScaleFlexible,
    enabledIsMinimumScaleFlexible,
    enabledIsMaximumScaleFlexible,
    setEnabledIsMaximumScaleFlexible,
  }: any) {
    return (
      <>
        <Row>
          <Col sm={4} className="mb-4">
            <FormInput
              label="Name (required)"
              name="Name"
              id="Name"
              readOnly={readOnly}
              instruction={undefined}
              onChange={undefined}
            />
          </Col>
          <Col sm={4} className="mb-4">
            <FormNumberInput
              label="Minimum Scale Value"
              id="MinimumScaleValue"
              name="MinimumScaleValue"
              readOnly={readOnly}
              postChange={(e: any) => {
                if (readOnly) return;

                const hasValue = !isValueEmpty(e);

                setEnabledIsMinimumScaleFlexible(hasValue);

                if (!hasValue) {
                  props.setFieldValue('IsMinimumScaleFlexible', true);
                }
              }}
              instruction={undefined}
              postBlur={undefined}
              warningMessage={undefined}
            />
          </Col>
          <Col sm={4} className="mb-4">
            <FormNumberInput
              label="Maximum Scale Value"
              id="MaximumScaleValue"
              name="MaximumScaleValue"
              readOnly={readOnly}
              postChange={(e: any) => {
                if (readOnly) return;

                const hasValue = !isValueEmpty(e);

                setEnabledIsMaximumScaleFlexible(hasValue);

                if (!hasValue) {
                  props.setFieldValue('IsMaximumScaleFlexible', true);
                }
              }}
              instruction={undefined}
              postBlur={undefined}
              warningMessage={undefined}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={4} className="mb-4">
            <FormSelect
              label="Unit (required)"
              id="unit"
              name="unit"
              readOnly={readOnly}
              isMulti={false}
              options={unitsOfMeasureDropdown}
              isClearable={true}
              placeholder={'Select ...'}
              textValue={undefined}
              instruction={undefined}
            />
          </Col>
          <Col sm={4} className="mb-4">
            <FormCheckbox
              label="Minimum Flexible"
              id="IsMinimumScaleFlexible"
              name="IsMinimumScaleFlexible"
              readOnly={readOnly || !enabledIsMinimumScaleFlexible}
              onCheck={undefined}
            />
          </Col>
          <Col sm={4} className="mb-4">
            <FormCheckbox
              label="Maximum Flexible"
              id="IsMaximumScaleFlexible"
              name="IsMaximumScaleFlexible"
              readOnly={readOnly || !enabledIsMaximumScaleFlexible}
              onCheck={undefined}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={4} className="mb-4">
            <FormNumberInput
              label="Tick Decimal Places"
              id="TickDecimalPlaces"
              name="TickDecimalPlaces"
              readOnly={readOnly}
              instruction={undefined}
              postChange={undefined}
              postBlur={undefined}
              warningMessage={undefined}
            />
          </Col>
        </Row>
      </>
    );
  }

  cleanFormValues(props: EntityProps): any {
    /* Do Nothing */
  }

  getActionMenu(event: any): React.ReactElement {
    return <></>;
  }

  getColumns(props: any): any {
    /* Do Nothing */
  }

  getFields(props: any): React.ReactElement {
    return <></>;
  }

  getValidations(props?: any): any {
    /* Do Nothing */
  }

  mapFormToDTO(props: EntityProps): any {
    const { entity, ...rest } = props;

    let IsMinimumScaleFlexible = 'Y';
    let IsMaximumScaleFlexible = 'Y';

    if (entity.MinimumScaleValue) {
      IsMinimumScaleFlexible = entity.IsMinimumScaleFlexible ? 'Y' : 'N';
    }

    if (entity.MaximumScaleValue) {
      IsMaximumScaleFlexible = entity.IsMaximumScaleFlexible ? 'Y' : 'N';
    }

    return {
      ...entity,
      IsMinimumScaleFlexible,
      IsMaximumScaleFlexible,
      MinimumScaleValue: isValueEmpty(entity.MinimumScaleValue) ? undefined : parseFloat(entity.MinimumScaleValue),
      MaximumScaleValue: isValueEmpty(entity.MaximumScaleValue) ? undefined : parseFloat(entity.MaximumScaleValue),
      UnitId: entity.unit?.value,
      TickDecimalPlaces: isValueEmpty(entity.TickDecimalPlaces) ? null : entity.TickDecimalPlaces,
      Name: isValueEmpty(entity.Name) ? undefined : entity.Name,
      unit: isValueEmpty(entity.unit) ? undefined : entity.unit,
    };
  }

  mapInitialValues(props: EntityProps): any {
    const { entity, unitsOfMeasureDropdown = [], ...rest } = props;

    return {
      ...entity,
      IsMinimumScaleFlexible: entity.IsMinimumScaleFlexible === 'Y',
      IsMaximumScaleFlexible: entity.IsMaximumScaleFlexible === 'Y',
      MinimumScaleValue: isValueEmpty(entity.MinimumScaleValue) ? null : parseFloat(entity.MinimumScaleValue),
      MaximumScaleValue: isValueEmpty(entity.MaximumScaleValue) ? null : parseFloat(entity.MaximumScaleValue),
      unit: isValueEmpty(entity.unit)
        ? unitsOfMeasureDropdown?.find((x: any) => x.value === entity.UnitId)
        : entity.unit,
      TickDecimalPlaces: entity.TickDecimalPlaces?.toString(),
    };
  }
}
